import {
  ALLOWANCES,
  BILLING_PERIOD_IDS,
  BILLING_PERIODS,
  DEFAULT_BILLING_PERIOD,
  FEATURES,
  PLAN_IDS,
  PLAN_PURPOSE,
  PLANS,
  type BillingPeriodId,
  type Feature,
  type PlanId,
} from '@buakei/pricing';
import { Button, Notice, cn } from '@buakei/ui';
import { Check, X } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { PriceBlock, signUpHref } from './plan-quote';

/**
 * Pricing (M1-08, BILL-002, ADR-010, ADR-011). Prices, entitlements and allowances all come
 * from `@buakei/pricing`. Each card lists every feature in the same order, with a check or a
 * dash and a readable status word, so plans compare row by row. Payments are not operational:
 * the page describes the intended offering, and choosing a plan opens sign-up without payment.
 */

const PERIOD_STORAGE_KEY = 'buakei.billing-period';

function readSavedPeriod(): BillingPeriodId {
  try {
    const saved = window.localStorage.getItem(PERIOD_STORAGE_KEY);
    return (BILLING_PERIOD_IDS as readonly string[]).includes(saved ?? '')
      ? (saved as BillingPeriodId)
      : DEFAULT_BILLING_PERIOD;
  } catch {
    return DEFAULT_BILLING_PERIOD;
  }
}

function savePeriod(period: BillingPeriodId) {
  try {
    window.localStorage.setItem(PERIOD_STORAGE_KEY, period);
  } catch {
    // A preference only; the page works without storage.
  }
}

/** Usage area: only approved values are shown; undefined allowances are never invented. */
function usageText(planId: PlanId): string {
  const a = ALLOWANCES[planId];
  const parts: string[] = [];
  if (a.aiCreditsPerMonth !== null) parts.push(`${a.aiCreditsPerMonth} AI credits a month`);
  if (a.storageGb !== null) parts.push(`${a.storageGb} GB document storage`);
  if (a.uploadLimit !== null) parts.push(a.uploadLimit);
  const scope =
    a.scope === 'personal'
      ? 'Usage is personal to your account.'
      : 'Usage is allocated to each paid member and grows with your team.';
  return parts.length > 0 ? `${parts.join(' · ')}. ${scope}` : scope;
}

/**
 * Features shown on the cards: what can be tried in the demo or is planned for launch.
 * Later-phase features stay recorded in the entitlement config but are not advertised
 * (owner instruction, ADR-011).
 */
const LISTED = FEATURES.filter((f) => f.status !== 'later');

/**
 * Free access today is the DEMO: it includes exactly the features that can be tried there,
 * with fictitious data and no account or payment. Launch Free-tier entitlements are an open
 * decision (ADR-011) and are not invented here.
 */
const freeIncludes = (f: Feature) => f.status === 'demo';

function BillingPeriodSelector({
  value,
  onChange,
}: {
  value: BillingPeriodId;
  onChange: (period: BillingPeriodId) => void;
}) {
  const name = useId();
  return (
    <fieldset>
      <legend className="sr-only">Billing period</legend>
      <div className="flex flex-col gap-1 rounded-2xl border border-line-200 bg-white p-1 sm:flex-row sm:rounded-full">
        {BILLING_PERIOD_IDS.map((id) => {
          const selected = id === value;
          return (
            <label
              key={id}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition-colors',
                'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-navy-900',
                selected ? 'bg-navy-900 text-white' : 'text-ink-900 hover:bg-navy-50',
              )}
            >
              <input
                type="radio"
                name={name}
                value={id}
                checked={selected}
                onChange={() => onChange(id)}
                className="sr-only"
              />
              {BILLING_PERIODS[id].label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** The plans in table order, each with its inclusion rule (Free = today's demo access). */
const COLUMNS: { id: string; name: string; includes: (f: Feature) => boolean }[] = [
  { id: 'free', name: 'Free', includes: freeIncludes },
  ...PLAN_IDS.map((planId) => ({
    id: planId,
    name: PLANS[planId].name,
    includes: (f: Feature) => f.plans.includes(planId),
  })),
];

/**
 * One shared feature list, with a column per plan (owner instruction): a green tick where a
 * plan includes the feature and a red cross where it does not. The meaning is also given as
 * text for screen readers, so it never depends on colour alone.
 */
function ComparisonTable() {
  return (
    <section aria-labelledby="compare-heading" className="flex flex-col gap-4">
      <h2 id="compare-heading" className="font-display text-3xl text-navy-900">
        What each plan includes
      </h2>
      <div className="overflow-hidden rounded-xl border border-line-200 bg-white">
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>
            <col />
            {COLUMNS.map((c) => (
              <col key={c.id} className="w-14 sm:w-24 lg:w-32" />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-line-200 bg-soft-white">
              <th scope="col" className="px-4 py-3 text-left font-semibold text-navy-900">
                Feature
              </th>
              {COLUMNS.map((c) => (
                <th
                  scope="col"
                  key={c.id}
                  className="px-1 py-3 text-center text-xs font-semibold text-navy-900 sm:text-sm"
                >
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LISTED.map((feature) => (
              <tr key={feature.id} className="border-b border-line-200 last:border-b-0">
                <th scope="row" className="px-4 py-3 text-left font-normal text-ink-900">
                  {feature.label}
                </th>
                {COLUMNS.map((c) =>
                  c.includes(feature) ? (
                    <td key={c.id} className="py-3 text-center">
                      <Check className="mx-auto size-5 text-ok-700" aria-hidden />
                      <span className="sr-only">Included in {c.name}</span>
                    </td>
                  ) : (
                    <td key={c.id} className="py-3 text-center">
                      <X className="mx-auto size-5 text-danger-700" aria-hidden />
                      <span className="sr-only">Not included in {c.name}</span>
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One card layout for every plan, so headings, prices and actions line up. */
function CardShell({
  id,
  name,
  purpose,
  price,
  usage,
  action,
}: {
  id: string;
  name: string;
  purpose: string;
  price: ReactNode;
  usage: string;
  action: ReactNode;
}) {
  const headingId = `plan-${id}`;
  return (
    <li
      aria-labelledby={headingId}
      className="flex flex-col rounded-xl border border-line-200 bg-white xl:row-span-4 xl:grid xl:grid-rows-subgrid xl:gap-0"
    >
      <div className="flex flex-col gap-2 p-6 pb-4">
        <h2 id={headingId} className="text-xl font-semibold text-navy-900">
          {name}
        </h2>
        <p className="leading-relaxed text-ink-900">{purpose}</p>
      </div>
      <div className="px-6 pb-4">{price}</div>
      <p className="mx-6 border-t border-line-200 py-3 text-sm text-ink-900">{usage}</p>
      <div className="p-6 pt-2">{action}</div>
    </li>
  );
}

function PlanCard({
  planId,
  period,
  noticeId,
}: {
  planId: PlanId;
  period: BillingPeriodId;
  noticeId: string;
}) {
  const plan = PLANS[planId];
  return (
    <CardShell
      id={planId}
      name={plan.name}
      purpose={PLAN_PURPOSE[planId]}
      price={<PriceBlock planId={planId} period={period} />}
      usage={usageText(planId)}
      action={
        // Opens sign-up with this plan selected; no payment is taken (ADR-010).
        <Button asChild variant="secondary" className="w-full">
          <Link to={signUpHref(planId, period)} aria-describedby={noticeId}>
            Choose {plan.name}
          </Link>
        </Button>
      }
    />
  );
}

/** Free: SLE 0 in every billing period; today this is the DEMO, with no account needed. */
function FreeCard() {
  return (
    <CardShell
      id="free"
      name="Free"
      purpose="Try Buakei AI with demonstration content before choosing a plan."
      price={
        <div className="flex flex-col gap-1">
          <p className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="font-display text-4xl text-navy-900">SLE 0</span>
            <span className="text-sm text-muted-600">/ month</span>
          </p>
          <p className="text-sm text-ink-900">No payment needed.</p>
        </div>
      }
      usage="Fictitious demonstration data only. Nothing you enter is saved."
      action={
        <Button asChild variant="secondary" className="w-full">
          <Link to="/sign-in">Explore the demo</Link>
        </Button>
      }
    />
  );
}

export function PricingPage() {
  const [period, setPeriod] = useState<BillingPeriodId>(readSavedPeriod);
  const noticeId = useId();

  useEffect(() => {
    document.title = 'Pricing · Buakei AI';
  }, []);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-10 px-4 py-16 md:py-20">
      <header className="flex flex-col items-center gap-6 text-center">
        <h1 className="font-display text-4xl leading-tight text-navy-900 md:text-5xl">
          Plans for research and study
        </h1>
        <BillingPeriodSelector
          value={period}
          onChange={(next) => {
            setPeriod(next);
            savePeriod(next);
          }}
        />
      </header>

      <div id={noticeId}>
        <Notice
          variant="announcement"
          title="Pricing is not yet available"
          className="mx-auto max-w-3xl"
        >
          These plans describe the intended offering at launch. Nothing can be purchased yet.
        </Notice>
      </div>

      <ul className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 xl:grid-rows-[auto_auto_1fr_auto] xl:gap-x-5 xl:gap-y-0">
        <FreeCard />
        {PLAN_IDS.map((planId) => (
          <PlanCard key={planId} planId={planId} period={period} noticeId={noticeId} />
        ))}
      </ul>

      <ComparisonTable />

      <p className="text-center text-sm text-muted-600">
        AI-heavy tasks will show an estimated cost before they run. Budget alerts warn you; they are
        not a guaranteed spending cap.
      </p>
    </div>
  );
}
